import { getGridModelsInfoOptions, getGridParameterOptions, getGridSurfaceOptions } from "@api";
import { makeCacheBustingQueryParam } from "@framework/utils/queryUtils";
import { sortTimeOrIntervalArray } from "@lib/utils/arrays";
import {
    getAvailableEnsembleIdentsForField,
    getAvailableRealizationsForEnsembleIdent,
} from "@modules/_shared/DataProviderFramework/dataProviders/dependencyFunctions/sharedSettingUpdaterFunctions";
import { NO_UPDATE } from "@modules/_shared/DataProviderFramework/delegates/_utils/Dependency";
import type {
    CustomDataProviderImplementation,
    DataProviderAccessors,
    FetchDataParams,
} from "@modules/_shared/DataProviderFramework/interfacesAndTypes/customDataProviderImplementation";
import type { SetupBindingsContext } from "@modules/_shared/DataProviderFramework/interfacesAndTypes/customSettingsHandler";
import type { MakeSettingTypesMap } from "@modules/_shared/DataProviderFramework/interfacesAndTypes/utils";
import { Setting } from "@modules/_shared/DataProviderFramework/settings/settingsDefinitions";
import type { RealizationGridData } from "@modules/_shared/DataProviderFramework/visualization/utils/types";
import {
    transformGridMappedProperty,
    transformGridSurface,
    type GridMappedProperty_trans,
    type GridSurface_trans,
} from "@modules/_shared/utils/queryDataTransforms";

const realizationGridSettings = [
    Setting.ENSEMBLE,
    Setting.REALIZATION,
    Setting.GRID_NAME,
    Setting.ATTRIBUTE,
    Setting.TIME_OR_INTERVAL,
    Setting.GRID_LAYER_RANGE,
    Setting.SHOW_GRID_LINES,
    Setting.COLOR_SCALE,
    Setting.OPACITY_PERCENT,
] as const;
export type RealizationGridSettings = typeof realizationGridSettings;
type SettingsWithTypes = MakeSettingTypesMap<RealizationGridSettings>;

export class RealizationGridProvider implements CustomDataProviderImplementation<
    RealizationGridSettings,
    RealizationGridData
> {
    settings = realizationGridSettings;

    getDefaultSettingsValues() {
        return {
            [Setting.SHOW_GRID_LINES]: false,
            [Setting.OPACITY_PERCENT]: 100,
        };
    }

    getDefaultName() {
        return "Grid Model 3D";
    }

    doSettingsChangesRequireDataRefetch(
        prevSettings: SettingsWithTypes | null,
        newSettings: SettingsWithTypes,
    ): boolean {
        if (prevSettings === null) {
            return true;
        }
        if (
            prevSettings[Setting.ENSEMBLE] !== newSettings[Setting.ENSEMBLE] ||
            prevSettings[Setting.REALIZATION] !== newSettings[Setting.REALIZATION] ||
            prevSettings[Setting.GRID_NAME] !== newSettings[Setting.GRID_NAME] ||
            prevSettings[Setting.ATTRIBUTE] !== newSettings[Setting.ATTRIBUTE] ||
            prevSettings[Setting.TIME_OR_INTERVAL] !== newSettings[Setting.TIME_OR_INTERVAL] ||
            prevSettings[Setting.GRID_LAYER_RANGE] !== newSettings[Setting.GRID_LAYER_RANGE]
        ) {
            return true;
        }
        return false;
    }

    makeValueRange({
        getData,
    }: DataProviderAccessors<RealizationGridSettings, RealizationGridData>): [number, number] | null {
        const data = getData();
        if (!data) {
            return null;
        }

        return [data.gridParameterData.min_grid_prop_value, data.gridParameterData.max_grid_prop_value];
    }

    fetchData({ getSetting, fetchQuery }: FetchDataParams<RealizationGridSettings, RealizationGridData>): Promise<{
        gridSurfaceData: GridSurface_trans;
        gridParameterData: GridMappedProperty_trans;
    }> {
        const ensembleIdent = getSetting(Setting.ENSEMBLE);
        const realizationNum = getSetting(Setting.REALIZATION);
        const gridName = getSetting(Setting.GRID_NAME);
        const attribute = getSetting(Setting.ATTRIBUTE);
        let timeOrInterval = getSetting(Setting.TIME_OR_INTERVAL);
        if (timeOrInterval === "NO_TIME") {
            timeOrInterval = null;
        }
        const range = getSetting(Setting.GRID_LAYER_RANGE);

        if (range === null) {
            throw new Error("Grid ranges are not set");
        }

        const gridParameterOptions = getGridParameterOptions({
            query: {
                case_uuid: ensembleIdent?.getCaseUuid() ?? "",
                ensemble_name: ensembleIdent?.getEnsembleName() ?? "",
                grid_name: gridName ?? "",
                parameter_name: attribute ?? "",
                parameter_time_or_interval_str: timeOrInterval,
                realization_num: realizationNum ?? 0,
                i_min: range[0][0],
                i_max: range[0][1],
                j_min: range[1][0],
                j_max: range[1][1],
                k_min: range[2][0],
                k_max: range[2][1],
                ...makeCacheBustingQueryParam(ensembleIdent ?? null),
            },
        });

        const gridSurfaceOptions = getGridSurfaceOptions({
            query: {
                case_uuid: ensembleIdent?.getCaseUuid() ?? "",
                ensemble_name: ensembleIdent?.getEnsembleName() ?? "",
                grid_name: gridName ?? "",
                realization_num: realizationNum ?? 0,
                i_min: range[0][0],
                i_max: range[0][1],
                j_min: range[1][0],
                j_max: range[1][1],
                k_min: range[2][0],
                k_max: range[2][1],
                ...makeCacheBustingQueryParam(ensembleIdent ?? null),
            },
        });

        const gridParameterPromise = fetchQuery(gridParameterOptions).then(transformGridMappedProperty);

        const gridSurfacePromise = fetchQuery(gridSurfaceOptions).then(transformGridSurface);

        return Promise.all([gridSurfacePromise, gridParameterPromise]).then(([gridSurfaceData, gridParameterData]) => ({
            gridSurfaceData,
            gridParameterData,
        }));
    }

    areCurrentSettingsValid({
        getSetting,
    }: DataProviderAccessors<RealizationGridSettings, RealizationGridData>): boolean {
        return (
            getSetting(Setting.ENSEMBLE) !== null &&
            getSetting(Setting.REALIZATION) !== null &&
            getSetting(Setting.GRID_NAME) !== null &&
            getSetting(Setting.ATTRIBUTE) !== null &&
            getSetting(Setting.GRID_LAYER_RANGE) !== null &&
            getSetting(Setting.TIME_OR_INTERVAL) !== null
        );
    }

    setupBindings({ setting, makeSharedResult, queryClient }: SetupBindingsContext<RealizationGridSettings>) {
        setting(Setting.ENSEMBLE).bindValueConstraints({
            read(read) {
                return {
                    fieldIdentifier: read.globalSetting("fieldId"),
                    ensembles: read.globalSetting("ensembles"),
                };
            },
            resolve({ fieldIdentifier, ensembles }) {
                return getAvailableEnsembleIdentsForField(fieldIdentifier, ensembles);
            },
        });

        setting(Setting.REALIZATION).bindValueConstraints({
            read(read) {
                return {
                    ensembleIdent: read.localSetting(Setting.ENSEMBLE),
                    realizationFilterFunction: read.globalSetting("realizationFilterFunction"),
                };
            },
            resolve({ ensembleIdent, realizationFilterFunction }) {
                return getAvailableRealizationsForEnsembleIdent(ensembleIdent, realizationFilterFunction);
            },
        });

        const gridData = makeSharedResult({
            debugName: "RealizationGridData",
            read(read) {
                return {
                    ensembleIdent: read.localSetting(Setting.ENSEMBLE),
                    realization: read.localSetting(Setting.REALIZATION),
                };
            },
            async resolve({ ensembleIdent, realization }, { abortSignal }) {
                if (!ensembleIdent || realization === null) {
                    return null;
                }

                return await queryClient.fetchQuery({
                    ...getGridModelsInfoOptions({
                        query: {
                            case_uuid: ensembleIdent.getCaseUuid(),
                            ensemble_name: ensembleIdent.getEnsembleName(),
                            realization_num: realization,
                            ...makeCacheBustingQueryParam(ensembleIdent),
                        },
                        signal: abortSignal,
                    }),
                });
            },
        });

        setting(Setting.GRID_NAME).bindValueConstraints({
            read(read) {
                return {
                    gridData: read.sharedResult(gridData),
                };
            },
            resolve({ gridData }) {
                if (!gridData) {
                    return [];
                }

                const availableGridNames = [...new Set(gridData.map((gridModelInfo) => gridModelInfo.grid_name))];

                return availableGridNames;
            },
        });

        setting(Setting.ATTRIBUTE).bindValueConstraints({
            read(read) {
                return {
                    gridName: read.localSetting(Setting.GRID_NAME),
                    gridData: read.sharedResult(gridData),
                };
            },
            resolve({ gridName, gridData }) {
                if (!gridName || !gridData) {
                    return [];
                }

                const gridAttributeArr =
                    gridData.find((gridModel) => gridModel.grid_name === gridName)?.property_info_arr ?? [];

                const availableGridAttributes = [
                    ...new Set(gridAttributeArr.map((gridAttribute) => gridAttribute.property_name)),
                ];

                return availableGridAttributes;
            },
        });

        setting(Setting.GRID_LAYER_RANGE).bindValueConstraints({
            read(read) {
                return {
                    gridName: read.localSetting(Setting.GRID_NAME),
                    gridData: read.sharedResult(gridData),
                };
            },
            resolve({ gridName, gridData }) {
                if (!gridName || !gridData) {
                    return NO_UPDATE;
                }

                const gridDimensions =
                    gridData.find((gridModel) => gridModel.grid_name === gridName)?.dimensions ?? null;
                if (!gridDimensions) {
                    return NO_UPDATE;
                }

                return {
                    range: {
                        i: [0, gridDimensions.i_count - 1, 1],
                        j: [0, gridDimensions.j_count - 1, 1],
                        k: [0, gridDimensions.k_count - 1, 1],
                    },
                    zones: gridDimensions.subgrids,
                };
            },
        });

        setting(Setting.TIME_OR_INTERVAL).bindValueConstraints({
            read(read) {
                return {
                    gridName: read.localSetting(Setting.GRID_NAME),
                    gridAttribute: read.localSetting(Setting.ATTRIBUTE),
                    gridData: read.sharedResult(gridData),
                };
            },
            resolve({ gridName, gridAttribute, gridData }) {
                if (!gridName || !gridAttribute || !gridData) {
                    return [];
                }

                const gridAttributeArr =
                    gridData.find((gridModel) => gridModel.grid_name === gridName)?.property_info_arr ?? [];

                return sortTimeOrIntervalArray(
                    Array.from(
                        new Set(
                            gridAttributeArr
                                .filter((attr) => attr.property_name === gridAttribute)
                                .map((gridAttribute) => gridAttribute.iso_date_or_interval ?? "NO_TIME"),
                        ),
                    ),
                );
            },
        });
    }
}
